import { Grid, useMediaQuery } from "@mui/material";
import { dripOfferAssets } from "assets/cdn";
import { useGoogleAnalyticsContext } from "contexts";
import { useImagePath } from "hooks";
import useRewardsById from "hooks/useRewardsById";
import { Node } from "interfaces/drip-offer";
import { Moment } from "moment";
import { getAvailablePrizes, getMissedDays } from "pages/DripOffer/hooks";
import { RedX } from "pages/DripOffer/styles";
import { useEffect } from "react";
import { getCount, getDisplay, getTimeLeft } from "utils/time";

interface SaleInfoProps {
	nodes: Node[];
	localTime: string;
	nowTime: Moment | null;
	endTime: string;
	isPopup?: boolean;
	isSmall?: boolean;
	claimDurationDays: number;
}

export const SaleInfo = ({
	nodes,
	localTime,
	isPopup,
	isSmall,
	nowTime,
	endTime,
	claimDurationDays
}: SaleInfoProps) => {
	const isLarge = useMediaQuery("(min-width: 753px)");
	const isLandscape = useMediaQuery("(orientation: landscape)");
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { getImage } = useImagePath("cdn");
	const xBoxImage = getImage(dripOfferAssets.boxWithX);
	const emptyBoxImage = getImage(dripOfferAssets.emptyBox);
	const safeNodes = nodes ?? [];

	const missedDays = getMissedDays(safeNodes, localTime);
	const availableDaysToMiss = claimDurationDays - safeNodes.length - 1;

	const furthestNode = safeNodes.findLast((n: Node) => n.state === "claimed");
	const currentNode = safeNodes.findLast((n: Node) => n.state === "available");
	const useNode = currentNode ?? furthestNode ?? safeNodes[0];
	const rewardType = useRewardsById(useNode?.rewardId ?? "");

	useEffect(() => {
		if (rewardType.length > 0) {
			const timeRemaining = getCount(nowTime, endTime);
			const timeToDisplay = getDisplay(timeRemaining);

			reportEvent("feature_rendered", {
				source: "drip_offer",
				furthest_prize_reward_id: furthestNode?.rewardId,
				total_Xs: missedDays,
				location: isPopup
					? "drip_offer_collect_popup"
					: "special_offer_section",
				time_to_claim: timeToDisplay,
				reward_type: `${rewardType?.[0]?.id}_${rewardType?.[0]?.description}`,
				claimed: useNode?.state === "claimed"
			});
			const timeLeft = getTimeLeft(nowTime, endTime, "seconds");
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "feature_rendered",
				event_type: "drip_offer",
				action: "feature_rendered",
				source: "drip_offer",
				reward_id: furthestNode?.rewardId,
				value_int: missedDays,
				section_name: isPopup
					? "drip_offer_collect_popup"
					: "special_offer_section",
				time_left: timeLeft,
				reward_type: `${rewardType?.[0]?.id}_${rewardType?.[0]?.description}`,
				status: useNode?.state === "claimed" ? "claimed" : "not claimed"
			});
		}
	}, [rewardType?.[0]?.id]);

	if (isSmall || isPopup) {
		return (
			<Grid
				item
				xs={8}
				zIndex={1}
				mx="auto"
				position="relative"
				height={isLandscape || isLarge ? "100%" : "auto"}
			>
				<Grid
					container
					className="sale-info"
					alignItems="center"
					justifyContent="center"
				>
					<Grid
						container
						alignItems="center"
						justifyContent="space-between"
						mr={{ xs: isSmall ? 0.8 : 0 }}
					>
						<Grid item xs={4.5} pl={0.5} display="flex" alignItems="center">
							<p className="title">MISSED DAYS</p>
						</Grid>
						<Grid item>
							<Grid container direction="row-reverse">
								{Array.from(Array(availableDaysToMiss)).map((_, i) => (
									<Grid
										item
										key={i}
										mx={0.3}
										sx={{ ":first-child": { mr: 0 } }}
										className="red-x"
									>
										{i < availableDaysToMiss - missedDays ? (
											<img
												src={emptyBoxImage}
												alt="Missed day box"
												width={"100%"}
											/>
										) : (
											<>
												<img
													src={emptyBoxImage}
													alt="Missed day box"
													width={"100%"}
												/>
												<RedX>
													<img
														src={xBoxImage}
														alt="Missed day box"
														width={"100%"}
													/>
												</RedX>
											</>
										)}
									</Grid>
								))}
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={6.2} pl={0.5}>
						<p className="title">PRIZES LEFT</p>
					</Grid>
					<Grid item xs={5.8}>
						<p>{getAvailablePrizes(nodes)}</p>
					</Grid>
				</Grid>
			</Grid>
		);
	} else {
		return (
			<Grid
				item
				minWidth={360}
				zIndex={1}
				mx="auto"
				mb={{ sm: 1, md: 3 }}
				position="relative"
			>
				<Grid container className="sale-info">
					<Grid item sm={6}>
						<p>PRIZES LEFT</p>
					</Grid>
					<Grid item sm={6}>
						<p>MISSED DAYS</p>
					</Grid>
					<Grid
						item
						sm={6}
						pl={0.5}
						display="flex"
						alignItems="center"
						justifyContent="center"
					>
						<p>{getAvailablePrizes(nodes)}</p>
					</Grid>
					<Grid item sm={6}>
						<Grid container direction="row-reverse" justifyContent="center">
							{Array.from(Array(availableDaysToMiss)).map((_, i) => (
								<Grid item key={i} mx={0.2} className="red-x">
									{i < availableDaysToMiss - missedDays ? (
										<img
											src={emptyBoxImage}
											alt="Missed day box"
											width={"100%"}
										/>
									) : (
										<>
											<img
												src={emptyBoxImage}
												alt="Missed day box"
												width={"100%"}
											/>
											<RedX>
												<img
													src={xBoxImage}
													alt="Missed day box"
													width={"100%"}
												/>
											</RedX>
										</>
									)}
								</Grid>
							))}
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		);
	}
};
